import type { LifeCycleStage } from "@/stores/project/types/enum";

/** The `emissionMetaData` fields this resolution reads — the REST project payload's shape. */
export interface IncludedPhasesMetaData {
  includedPhases?: number[] | null;
  includedPhasesTranslated?: string[] | null;
}

/**
 * Which lifecycle stages count as included, as labels ("A1A3", "A4", "A5", …).
 *
 * Three sources, in falling order of authority:
 *
 * 1. **`revisionStages`** — the labels the revision being VIEWED holds on its own calculation row
 *    (`useProjectRevisionsStore().readLifecycleStagesFor`). Non-null only while previewing a frozen
 *    revision, which is the one case the REST payload cannot answer: `GET api/BuildingProject/{id}`
 *    takes no revisionId, so it always describes the working state.
 * 2. **`meta.includedPhasesTranslated`** — the labels the server sent for the working state.
 * 3. **`meta.includedPhases`** — the same thing as numbers, mapped through the lifecycle-stage enum.
 *    Kept because the two write paths disagree about which of the two fields they fill in.
 *
 * Empty is not an answer at any level: an empty list means "this source has nothing to say", so the
 * resolution falls through to the next one rather than reporting that no stage is included. The
 * caller gets `[]` only when nothing knows anything, which every consumer reads as "no stages" —
 * safe, because it is also what an unloaded project looks like.
 */
export const resolveIncludedLifestages = (
  revisionStages: string[] | null,
  meta: IncludedPhasesMetaData | null | undefined,
  lifeCycleStages: LifeCycleStage[] | null | undefined
): string[] => {
  if (revisionStages && revisionStages.length > 0) return revisionStages;
  if (!meta) return [];

  if (Array.isArray(meta.includedPhasesTranslated) && meta.includedPhasesTranslated.length > 0) {
    return meta.includedPhasesTranslated;
  }

  const indices = meta.includedPhases;
  if (!indices || !lifeCycleStages) return [];
  return indices
    .map((phaseIndex) => lifeCycleStages[phaseIndex]?.Enum)
    .filter((stage): stage is NonNullable<typeof stage> => Boolean(stage));
};
